import React, { useState, useEffect, useRef } from "react";
import axios from "../../../api/axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Avatar,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon
} from '@mui/icons-material';

import useData from '../../../Hooks/useData';
import styles from './UserEditForm.module.css';
import { printUserBadge } from './UserBadgePrint';

// Get API base URL for serving images
const getApiBaseUrl = () => {
  // In production, use the deployed API URL
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://baptistmenapi.onrender.com';
  }
  // In development, use localhost
  return 'http://localhost:3001';
};

const UserEditForm = () => {
  
  const { auth } = useData();
  const token = auth?.token;
  const navigate = useNavigate();
  const { userid } = useParams();
  const hasFetchedData = useRef(false);
  
  const [formData, setFormData] = useState({
    FullName: '',
    Email: '',
    PreferredName: '',
    UserEnabled: true,
    BP_DOB: '',
    BP_TermsDate: '',
    Gender: '',
    RoleIDs: [],
    ProfileImage: '',
    BP_BGCheckDate: '',
    BP_BGCheckText: '',
    BP_BadgeFlagline: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [roles, setRoles] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Image upload states
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const genderOptions = [
    { value: '', label: 'Please select' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
  ];

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get('/api/basic/roles');
        const rolesList = response.data.map(role => ({
          value: role.RoleID,
          label: role.RoleDisplayName
        }));
        setRoles(rolesList);
      } catch (error) {
      }
    };
    fetchRoles();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userid || hasFetchedData.current) return;
      
      hasFetchedData.current = true;
      setIsLoading(true);
      setError(null);
      
      try {
        const currentToken = token || localStorage.getItem('token');
        const requestConfig = {
          withCredentials: true,
          headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
        };
        
        const userResult = await axios.get(`/api/users/${userid}`, requestConfig);
        const user = userResult.data;
        
        let userRoleIDs = [];
        if (user.CMS_UserRole && user.CMS_UserRole.length > 0) {
          userRoleIDs = user.CMS_UserRole.map(ur => ur.RoleID);
        }
        
        let fullName = user.FullName || '';
        if (!fullName && (user.FirstName || user.LastName)) {
          fullName = `${user.FirstName || ''} ${user.LastName || ''}`.trim();
        }
        
        let gender = '';
        let termsDateLocal = '';
        let bgCheckDateLocal = '';
        let bgCheckText = '';
        let badgeFlagline = '';
        let preferredName = '';
        try {
          const settingsResult = await axios.get(`/api/users/usersettings/${userid}`, requestConfig);
          if (settingsResult.data) {
            const genderMap = {
              '0': 'Female',
              '1': 'Male',
              '2': 'Female', // legacy: early registrations stored Female as 2
            };
            // Prefer the integer UserGender field (canonical); fall back to BP_P_Gender string
            const rawGender = settingsResult.data.UserGender != null
              ? settingsResult.data.UserGender
              : settingsResult.data.BP_P_Gender;
            gender = genderMap[String(rawGender)] || '';

            // Terms acceptance date (datetime-local)
            const rawTerms = settingsResult.data.BP_TermsDate;
            if (rawTerms) {
              const d = new Date(rawTerms);
              if (!isNaN(d.getTime())) {
                // datetime-local expects: YYYY-MM-DDTHH:mm
                const pad = (n) => String(n).padStart(2, '0');
                termsDateLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              }
            }

            // Background Check Date (datetime-local)
            const rawBGDate = settingsResult.data.BP_BGCheckDate;
            if (rawBGDate) {
              const d = new Date(rawBGDate);
              if (!isNaN(d.getTime())) {
                const pad = (n) => String(n).padStart(2, '0');
                bgCheckDateLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              }
            }

            // Background Check Notes
            bgCheckText = settingsResult.data.BP_BGCheckText || '';

            // Badge Flag Line
            badgeFlagline = settingsResult.data.BP_BadgeFlagline || '';

            // Preferred Name
            preferredName = settingsResult.data.UserNickName || '';

          }
        } catch (err) {
        }
        
        let formattedDOB = user.BP_DOB || '';
        if (formattedDOB) {
          try {
            const dateObj = new Date(formattedDOB);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              formattedDOB = `${year}-${month}-${day}`;
            }
          } catch (e) {
          }
        }
        
        // Prefer profile image returned by API (Azure URL stored on CMS_UserSettings.UserPicture)
        let profileImagePath = user.ProfileImage || user.userSettings?.UserPicture || '';
        if (!profileImagePath && user.MiddleName && user.MiddleName.startsWith('/uploads/')) {
          // Backward compatibility: legacy local uploads path stored on CMS_User.MiddleName
          profileImagePath = user.MiddleName;
        }
        
        setCurrentImage(profileImagePath);

        setFormData({
          FullName: fullName,
          Email: user.Email || user.UserName || '',
          PreferredName: preferredName,
          UserEnabled: user.UserEnabled !== false,
          BP_DOB: formattedDOB,
          BP_TermsDate: termsDateLocal,
          Gender: gender,
          RoleIDs: userRoleIDs,
          ProfileImage: profileImagePath,
          BP_BGCheckDate: bgCheckDateLocal,
          BP_BGCheckText: bgCheckText,
          BP_BadgeFlagline: badgeFlagline
        });
        
        
        setIsLoading(false);
      } catch (error) {
        setError('Failed to load user data');
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userid]); // Removed token dependency to prevent unnecessary re-fetches

  const handleFieldChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const buildAvatarSrc = (img) => {
    if (!img) return '';
    const base = String(img).startsWith('http') ? String(img) : `${getApiBaseUrl()}${img}`;
    const sep = base.includes('?') ? '&' : '?';
    const finalUrl = `${base}${sep}t=${Date.now()}`;
    return finalUrl;
  };

  // Azure-backed image upload handler
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    setIsUploadingImage(true);
    setError(null);

    try {
      const data = new FormData();
      data.append('file', file);

      const response = await axios.post(`/api/users/${userid}/profile-picture`, data, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: true
      });

      if (response.data?.success && response.data?.url) {
        setCurrentImage(response.data.url);
        setSnackbar({
          open: true,
          message: 'Image uploaded successfully!',
          severity: 'success'
        });
      } else {
        setError('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload image: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Remove image
  const handleRemoveImage = async () => {
    try {
      setIsUploadingImage(true);
      
      await axios.delete(`/api/users/${userid}/profile-picture`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: true
      });

      setCurrentImage('');
      setSnackbar({
        open: true,
        message: 'Image removed successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Remove image error:', error);
      setError('Failed to remove image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    
    // Validate required fields
    if (!formData.FullName || formData.FullName.trim() === '') {
      setError('Full Name is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const requestConfig = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      };
      
      // Fetch current user to preserve all fields
      const currentUserResponse = await axios.get(`/api/users/${userid}`, requestConfig);
      const currentUser = currentUserResponse.data;
      
      // Remove fields that shouldn't be sent to the user update endpoint
      const { userSettings, ProfileImage, CMS_UserRole, CMS_UserSettings, ...userDataBase } = currentUser;
      
      // Split full name
      const nameParts = formData.FullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Merge with updates
      const userData = {
        ...userDataBase,
        FirstName: firstName,
        LastName: lastName,
        FullName: formData.FullName,
        UserEnabled: formData.UserEnabled,
        BP_DOB: formData.BP_DOB || null,
        UserLastModified: new Date().toISOString()
      };
      
      // Note: Image is handled separately via upload endpoints
      // The MiddleName field is updated directly during image upload
      
      // Remove fields that shouldn't be updated
      delete userData.UserID;
      
      
      const updateResponse = await axios.put(`/api/users/${userid}`, userData, requestConfig);
      
      try {
        await axios.put(`/api/users/${userid}/role`, { RoleIDs: (formData.RoleIDs || []).map(Number) }, requestConfig);
      } catch (roleError) {
      }
      
      // Update gender if provided
      // Update user settings (gender + terms acceptance date)
      try {
        const settingsUpdateData = {};

        if (formData.Gender !== undefined && formData.Gender !== null && formData.Gender !== '') {
          const genderMap = {
            'Male': 1,
            'Female': 0,
          };
          const genderValue = genderMap[formData.Gender];
          settingsUpdateData.UserGender = genderValue;
          settingsUpdateData.BP_P_Gender = String(genderValue);
        }

        // Terms Acceptance Date is editable by BOM Admin users
        // Empty string clears the date (sets it to null).
        if (formData.BP_TermsDate !== undefined) {
          if (formData.BP_TermsDate && String(formData.BP_TermsDate).trim() !== '') {
            const d = new Date(formData.BP_TermsDate);
            settingsUpdateData.BP_TermsDate = !isNaN(d.getTime()) ? d.toISOString() : null;
          } else {
            settingsUpdateData.BP_TermsDate = null;
          }
        }

        // Background Check Date
        if (formData.BP_BGCheckDate !== undefined) {
          if (formData.BP_BGCheckDate && String(formData.BP_BGCheckDate).trim() !== '') {
            const d = new Date(formData.BP_BGCheckDate);
            settingsUpdateData.BP_BGCheckDate = !isNaN(d.getTime()) ? d.toISOString() : null;
          } else {
            settingsUpdateData.BP_BGCheckDate = null;
          }
        }

        // Background Check Notes
        if (formData.BP_BGCheckText !== undefined) {
          settingsUpdateData.BP_BGCheckText = formData.BP_BGCheckText || null;
        }

        // Badge Flag Line
        if (formData.BP_BadgeFlagline !== undefined) {
          settingsUpdateData.BP_BadgeFlagline = formData.BP_BadgeFlagline || null;
        }

        // Preferred Name (UserNickName)
        if (formData.PreferredName !== undefined) {
          settingsUpdateData.UserNickName = formData.PreferredName || null;
        }

        if (Object.keys(settingsUpdateData).length > 0) {
          await axios.put(`/api/users/usersettings/${userid}`, settingsUpdateData, requestConfig);
        }
      } catch (settingsError) {
        throw new Error('Failed to save user settings: ' + (settingsError.response?.data?.error || settingsError.message));
      }
      
      // Image data is handled separately and doesn't need refreshing here
      
      // Show success message
      setSuccess(true);
      //setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
            
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);

    } catch (error) {
      setError(error.response?.data?.error || error.message || "Failed to update user. Please try again.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Edit User</h1>
                <p>Manage user details</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Edit: {formData.FullName || 'User'}</h1>
              <p>Manage user details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        <div className="container-fluid">
        <Box className={styles.container}>
          {/* Back + Print Badge buttons row */}
          <Box className={styles.topButtonsRow}>
            <Button
              type="button"
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              className={styles.backButton}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => printUserBadge(userid, token)}
              className={styles.printBadgeButton}
            >
              Print Badge
            </Button>
          </Box>

      {error && (
        <Alert severity="error" className={styles.errorAlert} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className={styles.successAlert}>
          User updated successfully!
        </Alert>
      )}

      <Paper className={styles.formPaper}>
        <Box className={styles.profileSection}>
          <Typography variant="h6" className={styles.profileTitle}>
            PROFILE PICTURE
          </Typography>
          
          <Avatar
            src={buildAvatarSrc(currentImage)}
            className={styles.profileAvatar}
            onError={(e) => {
            }}
          />
          
          <Box className={styles.profileButtonsContainer}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              id="image-upload-input"
              disabled={isUploadingImage}
            />
            <label htmlFor="image-upload-input">
              <Button
                type="button"
                variant="contained"
                component="span"
                startIcon={isUploadingImage ? <CircularProgress size={20} color="inherit" /> : <EditIcon />}
                className={styles.changeImageButton}
                disabled={isUploadingImage}
              >
                {isUploadingImage ? 'UPLOADING...' : 'CHANGE IMAGE'}
              </Button>
            </label>
            
            {currentImage && (
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={handleRemoveImage}
                disabled={isUploadingImage}
              >
                REMOVE IMAGE
              </Button>
            )}
          </Box>
          
          <Typography variant="caption" className={styles.imageCaption}>
            JPG, PNG, GIF (Max 5MB)
          </Typography>
        </Box>

        <Typography variant="h6" className={styles.formTitle}>
          USER INFORMATION
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Full Name"
                name="FullName"
                value={formData.FullName}
                onChange={handleFieldChange}
                required
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Preferred Name"
                name="PreferredName"
                value={formData.PreferredName}
                onChange={handleFieldChange}
                helperText="Optional: Name to display on badge and in communications"
                className={styles.textField}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Email"
                name="Email"
                type="email"
                value={formData.Email}
                onChange={handleFieldChange}
                required
                disabled
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Date of Birth"
                name="BP_DOB"
                type="date"
                value={formData.BP_DOB}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <FormControl fullWidth variant="outlined">
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  name="RoleIDs"
                  value={formData.RoleIDs || []}
                  onChange={handleFieldChange}
                  label="Roles"
                  className={styles.selectField}
                  renderValue={(selected) =>
                    selected.map(id => roles.find(r => r.value === id)?.label || id).join(', ')
                  }
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <FormControl fullWidth variant="outlined">
                <InputLabel>Gender</InputLabel>
                <Select
                  name="Gender"
                  value={formData.Gender || ''}
                  onChange={handleFieldChange}
                  label="Gender"
                  displayEmpty
                  className={styles.selectField}
                >
                  {genderOptions.map((option) => (
                    <MenuItem 
                      key={option.value || 'empty'} 
                      value={option.value}
                      className={option.value === '' && formData.Gender ? styles.selectMenuItemHidden : styles.selectMenuItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Terms Acceptance Date"
                name="BP_TermsDate"
                type="datetime-local"
                value={formData.BP_TermsDate || ''}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.switchFieldContainer}>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.UserEnabled}
                    onChange={handleFieldChange}
                    name="UserEnabled"
                  />
                }
                label="User Enabled"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Background Check Date"
                name="BP_BGCheckDate"
                type="datetime-local"
                value={formData.BP_BGCheckDate || ''}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Background Check Notes"
                name="BP_BGCheckText"
                value={formData.BP_BGCheckText || ''}
                onChange={handleFieldChange}
                multiline
                rows={3}
                helperText="Enter comments about background check"
                className={styles.textareaField}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={styles.fieldContainer}>
              
              <TextField
                fullWidth
                label="Badge Flag Line"
                name="BP_BadgeFlagline"
                value={formData.BP_BadgeFlagline || ''}
                onChange={handleFieldChange}
                helperText="Optional text displayed on yellow flag (e.g., 'Tester', 'Staff', etc.)"
                className={styles.textField}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box className={styles.formActions}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? 'Updating...' : 'Update User'}
          </Button>
        </Box>
      </Paper>


      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          className={styles.snackbarAlert}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Box>
        </div>
      </div>
    </main>
  );
};

export default UserEditForm;
